<script setup lang="ts">
import { computed } from "vue";

import { signalSelected } from "@/analytics/events/search";
import { trackEvent } from "@/analytics/track";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import type { SignalItem } from "@/store/search/signals";
import { useSignalStore } from "@/store/search/signals";

import SignalHelp from "./SignalHelp.vue";

// The current board's signals as a multi-select list with help and counts.
const props = defineProps<{ source: "top_menu" | "search"; header?: string }>();
const emit = defineEmits<{ added: [] }>();

const store = useSearchMenuStore();
const signalStore = useSignalStore();

const selection = computed({
  get: () => signalStore.selectedItems,
  set: (items: SignalItem[]) => {
    const added = items.filter((item) => !store.selectedSignals.has(item.id));
    store.selectedSignals = new Set(items.map((item) => item.id));
    added.forEach((item) => trackEvent(signalSelected(item.id, props.source)));
    if (added.length) emit("added");
  },
});

// a selected signal stays enabled at 0 so it can be unticked
function isDisabled(item: SignalItem) {
  return item.count === 0 && !store.selectedSignals.has(item.id);
}
</script>

<template>
  <SelectableList
    v-model="selection"
    v-keyboard-nav
    :header
    :groups="signalStore.groups"
    :is-item-disabled="isDisabled"
    :loading="signalStore.loading"
  >
    <template #default="{ item }">{{ item.label }}</template>
    <template #after="{ item }">
      <span class="signal-row-end">
        <!-- pointer only: the row's aria-describedby carries the same text -->
        <BaseTooltip
          v-if="item.help"
          position="right"
          :fallback-positions="['bottom']"
          class="signal-info"
        >
          <SvgIcon name="message/info" width="16" height="16" aria-hidden="true" />
          <template #content>
            <SignalHelp :help="item.help" />
          </template>
        </BaseTooltip>
        {{ item.count }}
      </span>
    </template>
  </SelectableList>
</template>

<style lang="scss" scoped>
.signal-row-end {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-width: 16px; // the row's `after` minimum, so short counts don't get centred in it
}

// the info icon shows only on the hovered or keyboard-focused row
.signal-info {
  display: flex;
  visibility: hidden;
}

.menu-list-item:is(:hover, :focus-visible) .signal-info {
  visibility: visible;
}
</style>
